import { useStyles, Button, MessageTooltip } from '@archipro-design/aria';
import { FolderTransferLine, UserLight } from '@archipro-design/icons';
import * as S from './DetailPageButtons.style';
import * as React from 'react';
import { createPortal } from 'react-dom';
import DeleteItemConfirmModal from '~/modules/directory/component/DeleteItemConfirmModal';

export type VisibilityItemType = 'project' | 'product';

interface VisibilityToggleButtonProps {
    isInDirectory: boolean;
    itemType: VisibilityItemType;
    onToggle: (inLibrary: boolean) => void;
    /**
     * When true the button is read-only: clicks don't open the confirm
     * modal, the icon is forced to "Profile Only", and the tooltip
     * explains why. Use when the owning professional isn't in the
     * directory so the item's effective visibility is locked to profile.
     */
    disabled?: boolean;
    /** Extra line appended to the remove-from-directory confirm copy. */
    removalNote?: string;
}

const DISABLED_TOOLTIP = {
    project:
        'This project is profile only because its professional is not enabled or published. Enable the professional to make the project visible in the directory.',
    product:
        'This product is profile only because its professional is not enabled or published. Enable the professional to make the product visible in the directory.',
} as const;

const CONFIRM_COPY = {
    addToDirectory: {
        project:
            'Are you sure you want to add this project to the directory page? This project will also be visible in search results.',
        product:
            'Are you sure you want to add this product to the directory page? This product will also be visible in search results.',
    },
    removeFromDirectory: {
        project:
            'Removing this project from the directory will also hide it from search results. It will still be visible on profile.',
        product:
            'Removing this product from the directory will also hide it from search results. It will still be visible on profile.',
    },
} as const;

export const VisibilityToggleButton = ({
    isInDirectory,
    itemType,
    onToggle,
    disabled = false,
    removalNote,
}: VisibilityToggleButtonProps) => {
    const { css } = useStyles();
    const [tooltipOpen, setTooltipOpen] = React.useState(false);
    const [confirmOpen, setConfirmOpen] = React.useState(false);

    React.useEffect(() => {
        if (confirmOpen) {
            document.documentElement.style.scrollbarGutter = 'stable';
        } else {
            document.documentElement.style.scrollbarGutter = '';
        }
        return () => {
            document.documentElement.style.scrollbarGutter = '';
        };
    }, [confirmOpen]);

    const effectiveInDirectory = isInDirectory && !disabled;
    const icon = effectiveInDirectory ? <FolderTransferLine /> : <UserLight />;
    const tooltipLabel = disabled
        ? DISABLED_TOOLTIP[itemType]
        : isInDirectory
          ? 'In Directory'
          : 'Profile Only';

    const confirmContent = isInDirectory
        ? [CONFIRM_COPY.removeFromDirectory[itemType], removalNote]
              .filter(Boolean)
              .join(' ')
        : CONFIRM_COPY.addToDirectory[itemType];

    const handleConfirm = () => {
        setConfirmOpen(false);
        onToggle(!isInDirectory);
    };

    const handleClick = () => {
        setTooltipOpen(false);
        if (disabled) return;
        setConfirmOpen(true);
    };

    return (
        <>
            <MessageTooltip
                open={tooltipOpen && !confirmOpen}
                trigger={
                    <div
                        onMouseEnter={() => setTooltipOpen(true)}
                        onMouseLeave={() => setTooltipOpen(false)}
                        onMouseDown={() => setTooltipOpen(false)}
                    >
                        <Button
                            className={css(S.ActionButton)}
                            onClick={handleClick}
                            icon={icon}
                            size={30}
                            color="white"
                            variables={{
                                iconPadding: 0,
                                buttonPadding: 16,
                                buttonRadius: `50%`,
                            }}
                        />
                    </div>
                }
                content={{
                    children: tooltipLabel,
                    className: css(S.TooltipContent),
                    size: 18,
                    padding: 12,
                    color: 'white',
                }}
                position="after"
                offset={[1, 9]}
            />
            {confirmOpen &&
                typeof document !== 'undefined' &&
                createPortal(
                    <DeleteItemConfirmModal
                        open={confirmOpen}
                        title={
                            isInDirectory
                                ? 'Remove from directory?'
                                : 'Add to directory?'
                        }
                        content={confirmContent}
                        onCancel={() => setConfirmOpen(false)}
                        onConfirm={handleConfirm}
                        confirmLabel="Continue"
                        cancelLabel="Cancel"
                    />,
                    document.body,
                )}
        </>
    );
};
